import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Users, Plus, Trash2, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssigneeManagement() {
  const { t } = useLanguage();
  const [assignees, setAssignees] = useState([]);
  const [newAssignee, setNewAssignee] = useState({ firstName: '', lastName: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAssignees();
  }, []);

  const fetchAssignees = async () => {
    try {
      const response = await axios.get(`${API}/assignees`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAssignees(response.data);
    } catch (error) {
      console.error('Error fetching assignees:', error);
      toast.error('Failed to load assignees');
    }
  };

  const handleAddAssignee = async (e) => {
    e.preventDefault();
    if (!newAssignee.firstName.trim() || !newAssignee.lastName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${newAssignee.firstName} ${newAssignee.lastName}`;
      
      const response = await axios.post(`${API}/assignees`, {
        name: fullName,
        role: 'Technician'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success(`Assignee ${fullName} added successfully`);
      setNewAssignee({ firstName: '', lastName: '' });
      fetchAssignees();
    } catch (error) {
      console.error('Error adding assignee:', error);
      let errorMessage = 'Failed to add assignee';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignee = async (assigneeId) => {
    if (!window.confirm('Are you sure you want to remove this assignee?')) {
      return;
    }

    try {
      await axios.delete(`${API}/assignees/${assigneeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      toast.success('Assignee removed successfully');
      fetchAssignees();
    } catch (error) {
      console.error('Error deleting assignee:', error);
      toast.error('Failed to remove assignee');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-600" />
              {t('assigneeManagement')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('manageTechnicians')}
            </p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            {assignees.length} {t('assignees')}
          </Badge>
        </div>
      </div>

      {/* Add New Assignee Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Plus className="w-5 h-5 mr-2" />
            {t('addAssignee')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddAssignee} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                  {t('name')} *
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={newAssignee.firstName}
                  onChange={(e) => setNewAssignee(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder={t('enterName')}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                  {t('lastName')} *
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={newAssignee.lastName}
                  onChange={(e) => setNewAssignee(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="e.g., Doe"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Assignee
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Assignees List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <UserCheck className="w-5 h-5 mr-2" />
            Current Assignees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {assignees.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No assignees added yet. Add your first assignee above.
              </p>
            ) : (
              assignees.map((assignee) => (
                <div
                  key={assignee.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        {assignee.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{assignee.name}</p>
                      <p className="text-sm text-gray-500">{assignee.role}</p>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAssignee(assignee.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AssigneeManagement;
